import { ActionsCellRenderer } from './ActionsCellRenderer';
import { getBlotterLegsQueryOptions } from './getBlotterLegsQueryOptions';
import type { Instant } from '@js-joda/core';
import { FieldTypeCategory, NumericUnit } from '@thinkalpha/language-services';
import type {
    Order,
    OrderDuration,
    OrderExecutionUpdate,
    OrderType,
    OrderStatus,
    OrderUpdate,
} from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { TFunction } from 'i18next';
import { ColumnHeaderTooltip } from 'src/components/table-view/ColumnHeaderTooltip';
import { makeSideCellRenderer } from 'src/features/legsHoverCard/makeSideCellRenderer';
import type { OrderLeg } from 'src/features/legsHoverCard/types';
import { instrumentToString } from 'src/lib/instrumentUtils';
import { getOrderStatusDictionary, OPEN_ORDERS } from 'src/lib/orders/getOrderStatusValues';
import { instrumentRenderer } from 'src/lib/renderers/instrumentRenderer';
import type { StaticGridColumn, StaticGridSettings } from 'src/types';

const timeComparator = (leftInstant: Instant | undefined, rightInstant: Instant | undefined) => {
    // Some orders to do not have a time for a value
    // (ie: transactTime does not exist on pending orders)
    // so when sorting, it makes sense to treat them as 'now' this way
    if (!leftInstant) return 1;
    if (!rightInstant) return -1;

    return leftInstant.compareTo(rightInstant);
};

/**
 * The UI will query orders and add account name before displaying them
 */
export interface OrderWithMetaData extends Order {
    accountName: string;
    firmName: string;
    firmTradingSystemName: string;
}

type BlotterGridColumn = keyof BlotterProcessedRow | 'actions' | 'firmName' | 'firmTradingSystemName';

export const BlotterGridDefaultColDefData: Partial<Record<BlotterGridColumn, Partial<StaticGridColumn>>> = {
    actions: {
        headerName: 'trading::blotter::headers::actions',
        headerTooltip: 'trading::blotter::headers::actions',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'actions',
        field: 'actions',
        resizable: true,
        sortable: false,
        cellRenderer: ActionsCellRenderer,
        initialWidth: 100,
        suppressColumnsToolPanel: true,
        isControlColumn: true,
    },

    createdAt: {
        headerName: 'trading::blotter::headers::timeEntry',
        headerTooltip: 'trading::blotter::headers::timeEntry',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'createdAt',
        field: 'createdAt',
        resizable: true,
        sort: 'desc',
        hide: false,
        sortable: true,
        comparator: timeComparator,
    },

    status: {
        headerName: 'trading::blotter::headers::status',
        headerTooltip: 'trading::blotter::headers::status',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'status',
        field: 'status',
        sortable: true,
        resizable: true,
        hide: false,
    },

    instrument: {
        headerName: 'trading::blotter::headers::symbol',
        headerTooltip: 'trading::blotter::headers::symbol',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'instrument',
        field: 'instrument',
        sortable: true,
        hide: false,
        resizable: true,
        initialWidth: 150,
        cellRenderer: instrumentRenderer,
    },

    side: {
        headerName: 'trading::blotter::headers::side',
        headerTooltip: 'trading::blotter::headers::side',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'side',
        field: 'side',
        sortable: true,
        hide: false,
        resizable: true,
        cellRenderer: makeSideCellRenderer<BlotterProcessedRow>({
            sideField: 'side',
            createQueryOptions: (rowData) => getBlotterLegsQueryOptions(rowData.id, rowData.legs),
        }),
    },

    quantity: {
        headerName: 'trading::blotter::headers::qty',
        headerTooltip: 'trading::blotter::headers::qty',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'quantity',
        field: 'quantity',
        sortable: true,
        hide: false,
        resizable: true,
    },

    duration: {
        headerName: 'trading::blotter::headers::tif',
        headerTooltip: 'trading::blotter::headers::tif',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'duration',
        field: 'duration',
        sortable: true,
        hide: false,
        resizable: true,
    },

    expireTime: {
        headerName: 'trading::blotter::headers::expireTime',
        headerTooltip: 'trading::blotter::headers::expireTime',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'expireTime',
        field: 'expireTime',
        sortable: true,
        hide: false,
        resizable: true,
        comparator: timeComparator,
    },

    accountName: {
        headerName: 'trading::blotter::headers::actName',
        headerTooltip: 'trading::blotter::headers::actName',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'accountName',
        field: 'accountName',
        sortable: true,
        hide: false,
        resizable: true,
    },

    firmName: {
        headerName: 'trading::blotter::headers::firmName',
        headerTooltip: 'trading::blotter::headers::firmName',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'firmName',
        field: 'firmName',
        sortable: true,
        hide: false,
        resizable: true,
    },

    firmTradingSystemName: {
        headerName: 'trading::blotter::headers::firmTradingSystemName',
        headerTooltip: 'trading::blotter::headers::firmTradingSystemName',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'firmTradingSystemName',
        field: 'firmTradingSystemName',
        sortable: true,
        hide: false,
        resizable: true,
    },

    account: {
        headerName: 'trading::blotter::headers::actID',
        headerTooltip: 'trading::blotter::headers::actID',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'account',
        field: 'account',
        sortable: true,
        hide: false,
        resizable: true,
    },

    type: {
        headerName: 'trading::blotter::headers::typeOrder',
        headerTooltip: 'trading::blotter::headers::typeOrder',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'type',
        field: 'type',
        sortable: true,
        hide: false,
        resizable: true,
    },

    price: {
        headerName: 'trading::blotter::headers::price',
        headerTooltip: 'trading::blotter::headers::price',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'price',
        field: 'price',
        sortable: true,
        hide: false,
        resizable: true,
    },

    clOrdId: {
        headerName: 'trading::blotter::headers::idOrder',
        headerTooltip: 'trading::blotter::headers::idOrder',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'clOrdId',
        field: 'clOrdId',
        sortable: true,
        hide: false,
        resizable: true,
    },

    lastError: {
        headerName: 'trading::blotter::headers::ordRejReason',
        headerTooltip: 'trading::blotter::headers::ordRejReason',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'lastError',
        field: 'lastError',
        sortable: true,
        hide: false,
        resizable: true,
    },

    lastContraBroker: {
        headerName: 'trading::blotter::headers::contra',
        headerTooltip: 'trading::blotter::headers::contra',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'lastContraBroker',
        field: 'lastContraBroker',
        sortable: true,
        hide: false,
        resizable: true,
    },

    lastShares: {
        headerName: 'trading::blotter::headers::sharesLast',
        headerTooltip: 'trading::blotter::headers::sharesLast',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'lastShares',
        field: 'lastShares',
        sortable: true,
        hide: false,
        resizable: true,
    },

    lastPx: {
        headerName: 'trading::blotter::headers::priceLast',
        headerTooltip: 'trading::blotter::headers::priceLast',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'lastPx',
        field: 'lastPx',
        sortable: true,
        hide: false,
        resizable: true,
    },

    leavesQty: {
        headerName: 'trading::blotter::headers::qtyLeaves',
        headerTooltip: 'trading::blotter::headers::qtyLeaves',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'leavesQty',
        field: 'leavesQty',
        sortable: true,
        hide: false,
        resizable: true,
    },

    avgPx: {
        headerName: 'trading::blotter::headers::priceAvg',
        headerTooltip: 'trading::blotter::headers::priceAvg',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'avgPx',
        field: 'avgPx',
        sortable: true,
        hide: false,
        resizable: true,
    },

    transactTime: {
        headerName: 'trading::blotter::headers::timeExec',
        headerTooltip: 'trading::blotter::headers::timeExec',
        tooltipComponent: ColumnHeaderTooltip,
        colId: 'transactTime',
        field: 'transactTime',
        sortable: true,
        hide: false,
        resizable: true,
        comparator: timeComparator,
    },
};

export const BlotterGridDefaults: StaticGridSettings = {
    type: 'SimpleGrid',
    columns: [
        {
            ...BlotterGridDefaultColDefData.actions,
            colId: 'actions',
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    formatting: {},
                    id: 'default__blotter__actions',
                    styles: {},
                },
            ],
            id: 'actions',
            name: 'Actions',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.createdAt,
            colId: 'createdAt',
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    formatting: {
                        date: {
                            format: 'monthsAndDays',
                            isHidden: false,
                        },
                        time: {
                            format: 'withoutSeconds',
                            isHidden: false,
                        },
                    },
                    id: 'default__blotter__createdAt',
                    styles: {},
                },
            ],
            id: 'createdAt',
            name: 'Created At',
            dataType: FieldTypeCategory.Timestamp,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.status,
            visible: true,
            displaySettings: [
                {
                    condition: {
                        type: 'noop',
                    },
                    formatting: {},
                    styles: {
                        text: {
                            align: 'right',
                        },
                    },
                    id: 'default__blotter__status',
                },
                {
                    condition: {
                        matchesValue: 'NEW',
                        type: 'matchesValue',
                    },
                    formatting: {},
                    styles: {
                        background: {},
                        cellStyles: {},
                        font: {},
                        rowBackground: {},
                        text: {
                            color: '#edf64d',
                        },
                    },
                    id: '55b48ad6-1ab4-4dd6-8840-873701ae2399',
                },
                {
                    condition: {
                        matchesValue: 'PARTIAL',
                        type: 'matchesValue',
                    },
                    formatting: {},
                    styles: {
                        background: {},
                        cellStyles: {},
                        font: {},
                        rowBackground: {},
                        text: {
                            color: '#f1fa40',
                        },
                    },
                    id: '83d05cfa-7b65-40b9-a773-95e93c72b12e',
                },
                {
                    condition: {
                        matchesValue: 'PENDING',
                        type: 'matchesValue',
                    },
                    formatting: {},
                    styles: {
                        background: {},
                        cellStyles: {},
                        font: {},
                        rowBackground: {},
                        text: {
                            color: '#bd4af3',
                        },
                    },
                    id: 'bd6a4599-2f42-42b1-859e-3f44376170bd',
                },
                {
                    condition: {
                        matchesValue: 'REJECTED',
                        type: 'matchesValue',
                    },
                    formatting: {},
                    styles: {
                        background: {},
                        cellStyles: {},
                        font: {},
                        rowBackground: {},
                        text: {
                            color: 'var(--color-red-500)',
                        },
                    },
                    id: '06632413-9700-407e-a242-d3854e13dcca',
                },
                {
                    condition: {
                        matchesValue: 'ACCEPTED',
                        type: 'matchesValue',
                    },
                    formatting: {},
                    styles: {
                        background: {},
                        cellStyles: {},
                        font: {},
                        rowBackground: {},
                        text: {
                            color: 'var(--color-green-500)',
                        },
                    },
                    id: 'babbe516-4053-4b93-afaa-a81950dcf122',
                },
                {
                    condition: {
                        matchesValue: getOrderStatusDictionary().suspended,
                        type: 'matchesValue',
                    },
                    formatting: {},
                    styles: {
                        background: {},
                        cellStyles: {},
                        font: {},
                        rowBackground: {},
                        text: {
                            color: 'var(--color-orange-500)',
                        },
                    },
                    id: 'status__suspended',
                },
            ],
            id: 'status',
            name: 'Status',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.instrument,
            visible: true,
            displaySettings: [
                {
                    condition: {
                        type: 'noop',
                    },
                    formatting: {},
                    styles: {
                        text: {
                            color: '#a08c24',
                            align: 'left',
                        },
                        font: {},
                        background: {},
                    },
                    id: 'default__blotter__instrument__symbol',
                },
            ],
            id: 'instrument',
            name: 'Symbol',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.side,
            visible: true,
            displaySettings: [
                {
                    condition: {
                        type: 'noop',
                    },
                    formatting: {},
                    styles: {
                        text: {
                            align: 'right',
                            transform: 'uppercase',
                        },
                    },
                    id: 'default__blotter__side',
                },
                {
                    condition: {
                        matchesValue: 'BUY',
                        type: 'matchesValue',
                    },
                    formatting: {},
                    styles: {
                        background: {},
                        cellStyles: {},
                        font: {},
                        rowBackground: {},
                        text: {
                            color: 'var(--color-green-500)',
                        },
                    },
                    id: '655a0ec9-05c6-4fbe-ba52-9b710e0228c9',
                },
                {
                    condition: {
                        matchesValue: 'SELL',
                        type: 'matchesValue',
                    },
                    formatting: {},
                    styles: {
                        background: {},
                        cellStyles: {},
                        font: {},
                        rowBackground: {},
                        text: {
                            color: 'var(--color-red-500)',
                        },
                    },
                    id: '9190f2ee-879e-42fe-beee-9c62dbf031a7',
                },
                {
                    condition: {
                        matchesValue: 'SELL-SHORT',
                        type: 'matchesValue',
                    },
                    formatting: {},
                    styles: {
                        background: {},
                        cellStyles: {},
                        font: {},
                        rowBackground: {},
                        text: {
                            color: 'var(--color-red-500)',
                        },
                    },
                    id: '1e37721e-a0d7-4d0c-83a2-836ea981bcb9',
                },
            ],
            id: 'side',
            name: 'Side',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.quantity,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__quantity',
                    formatting: {
                        number: {
                            locale: 'system',
                            decimalAmount: {
                                max: 0,
                                min: 0,
                            },
                        },
                    },
                    styles: {
                        text: {
                            align: 'right',
                        },
                    },
                },
            ],
            id: 'quantity',
            name: 'Quantity',
            dataType: FieldTypeCategory.Double,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.type,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__order__type',
                    formatting: {},
                    styles: {
                        text: {
                            align: 'right',
                        },
                    },
                },
            ],
            id: 'type',
            name: 'Order Type',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.duration,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__duration',
                    formatting: {},
                    styles: {
                        text: {
                            align: 'right',
                        },
                    },
                },
            ],
            id: 'duration',
            name: 'Duration',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.expireTime,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__expireTime',
                    formatting: {
                        date: {
                            format: 'monthsAndDays',
                            isHidden: false,
                        },
                        time: {
                            format: 'withoutSeconds',
                            isHidden: false,
                        },
                    },
                    styles: {},
                },
            ],
            id: 'expireTime',
            name: 'Expire Time',
            dataType: FieldTypeCategory.Timestamp,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.transactTime,
            colId: 'transactTime',
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    formatting: {
                        date: {
                            isHidden: true,
                        },
                        time: {
                            format: 'full',
                            isHidden: false,
                        },
                    },
                    id: 'default__blotter__time__exec',
                    styles: {
                        text: {
                            align: 'right',
                        },
                    },
                },
            ],
            id: 'transactTime',
            name: 'TimeExec',
            dataType: FieldTypeCategory.Timestamp,
            unit: null,
        },

        // {
        //     ...BlotterGridDefaultColDefData.target,
        //     visible: true,
        //     displaySettings: [
        //         {
        //             condition: { type: 'noop' },
        //             id: 'default__blotter__target',
        //             formatting: {
        //                 number: {
        //                     locale: 'system',
        //                     unitDisplay: 'none',
        //                 },
        //             },
        //             styles: {
        //                 text: {
        //                     align: 'right',
        //                 },
        //             },
        //         },
        //     ],
        //     id: 'target',
        //     name: 'Order Price',
        //     dataType: FieldTypeCategory.Double,
        //     unit: NumericUnit.price,
        // },

        {
            ...BlotterGridDefaultColDefData.lastContraBroker,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__contra',
                    formatting: {},
                    styles: {},
                },
            ],
            id: 'lastContraBroker',
            name: 'Contra',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.accountName,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__account_name',
                    formatting: {},
                    styles: {
                        text: {
                            align: 'left',
                        },
                    },
                },
            ],
            id: 'accountName',
            name: 'Account Name',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.account,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__account',
                    formatting: {},
                    styles: {
                        text: {
                            align: 'left',
                        },
                    },
                },
            ],
            id: 'account',
            name: 'Account ID',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.firmName,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__firm_name',
                    formatting: {},
                    styles: {
                        text: {
                            align: 'left',
                        },
                    },
                },
            ],
            id: 'firmName',
            name: 'Firm Name',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.firmTradingSystemName,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__firm_trading_system_name',
                    formatting: {},
                    styles: {
                        text: {
                            align: 'left',
                        },
                    },
                },
            ],
            id: 'firmTradingSystemName',
            name: 'Firm Short Name',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.clOrdId,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__order__id',
                    formatting: {},
                    styles: {},
                },
            ],
            id: 'clOrdId',
            name: 'IDorder',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.lastError,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__last__error',
                    formatting: {},
                    styles: {},
                },
            ],
            id: 'lastError',
            name: 'OrdRejReason',
            dataType: FieldTypeCategory.String,
            unit: null,
        },
        {
            ...BlotterGridDefaultColDefData.lastShares,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__shares__last',
                    formatting: {
                        number: {
                            locale: 'system',
                            decimalAmount: {
                                max: 0,
                                min: 0,
                            },
                        },
                    },
                    styles: {
                        text: {
                            align: 'right',
                        },
                    },
                },
            ],
            id: 'lastShares',
            name: 'SharesLast',
            dataType: FieldTypeCategory.Int,
            unit: NumericUnit.quantity,
        },
        {
            ...BlotterGridDefaultColDefData.lastPx,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__price__last',
                    formatting: {
                        number: {
                            locale: 'system',
                            decimalAmount: {
                                max: 2,
                                min: 2,
                            },
                            unitDisplay: 'none',
                        },
                    },
                    styles: {
                        text: {
                            align: 'right',
                        },
                    },
                },
            ],
            id: 'lastPx',
            name: 'PriceLast',
            dataType: FieldTypeCategory.Double,
            unit: NumericUnit.price,
        },
        {
            ...BlotterGridDefaultColDefData.leavesQty,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__qty__leaves',
                    formatting: {
                        number: {
                            locale: 'system',
                            decimalAmount: {
                                max: 0,
                                min: 0,
                            },
                        },
                    },
                    styles: {
                        text: {
                            align: 'right',
                        },
                    },
                },
            ],
            id: 'leavesQty',
            name: 'QtyLeaves',
            dataType: FieldTypeCategory.Int,
            unit: NumericUnit.quantity,
        },
        {
            ...BlotterGridDefaultColDefData.avgPx,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__price__avg',
                    formatting: {
                        number: {
                            locale: 'system',
                            unitDisplay: 'none',
                        },
                    },
                    styles: {
                        text: {
                            align: 'right',
                        },
                    },
                },
            ],
            id: 'avgPx',
            name: 'PriceAvg',
            dataType: FieldTypeCategory.Double,
            unit: NumericUnit.price,
        },
        {
            ...BlotterGridDefaultColDefData.price,
            visible: true,
            displaySettings: [
                {
                    condition: { type: 'noop' },
                    id: 'default__blotter__price',
                    formatting: {
                        number: {
                            locale: 'system',
                            unitDisplay: 'none',
                        },
                    },
                    styles: {
                        text: {
                            align: 'right',
                        },
                    },
                },
            ],
            id: 'price',
            name: 'Price',
            dataType: FieldTypeCategory.Double,
            unit: NumericUnit.price,
        },
    ],
    gridDisplaySettings: [
        {
            condition: {
                type: 'noop',
            },
            formatting: {
                number: {
                    locale: 'system',
                    decimalAmount: {
                        max: 2,
                        min: 2,
                    },
                },
            },
            styles: {
                text: {
                    align: 'center',
                },
            },

            id: 'blotter__grid__default',
        },
    ],
    id: 'blotter__display',
    rowBasedDisplaySettings: [],
    // rowBasedDisplaySettings: [
    //     {
    //         id: 'status__pending',
    //         condition: {
    //             type: 'matchesValue',
    //             originatingFieldId: 'status',
    //             matchesValue: OrderStatus.pending,
    //         },
    //         styles: {
    //             text: {
    //                 color: '#FFFF00',
    //             },
    //         },
    //         formatting: {},
    //     },
    //     {
    //         id: 'status__filled',
    //         condition: {
    //             type: 'matchesValue',
    //             originatingFieldId: 'Status',
    //             matchesValue: OrderStatus.filled,
    //         },
    //         styles: {
    //             text: {
    //                 color: '#00B0F0',
    //             },
    //         },
    //         formatting: {},
    //     },
    //     {
    //         id: 'status__partially__filled',
    //         condition: {
    //             type: 'matchesValue',
    //             originatingFieldId: 'Status',
    //             matchesValue: OrderStatus.partiallyFilled,
    //         },
    //         styles: {
    //             text: {
    //                 color: '#FFFF00',
    //             },
    //         },
    //         formatting: {},
    //     },

    //     {
    //         id: 'status__canceled',
    //         condition: {
    //             type: 'matchesValue',
    //             originatingFieldId: 'Status',
    //             matchesValue: OrderStatus.canceled,
    //         },
    //         styles: {
    //             text: {
    //                 color: '#FF0000',
    //             },
    //         },
    //         formatting: {},
    //     },
    //     {
    //         id: 'status__rejected',
    //         condition: {
    //             type: 'matchesValue',
    //             originatingFieldId: 'Status',
    //             matchesValue: OrderStatus.rejected,
    //         },
    //         styles: {
    //             text: {
    //                 color: '#FF0000',
    //             },
    //         },
    //         formatting: {},
    //     },
    // ],
};

const isOrderExecutionUpdate = (update: any): update is OrderExecutionUpdate => {
    return update?.quantity !== undefined;
};

export interface BlotterProcessedRow {
    actions: 'cancel'[]; // Actions are handled by the ActionsCellRenderer
    createdAt: Instant;
    /** Displayed status; order logic uses {@link BlotterProcessedRow.orderStatus} */
    status: BlotterDisplayStatus;
    orderStatus: OrderStatus;
    side: string;
    quantity: number | undefined;
    duration: OrderDuration | undefined;
    account: string;
    accountName: string;
    firmName: string;
    firmTradingSystemName: string;
    type: OrderType;
    price: number | null | undefined;
    stopPrice: number | null;
    id: string;
    updates: OrderUpdate[];
    lastError: string | undefined;
    lastContraBroker: string | null;
    lastShares: number | null;
    lastPx: number | null;
    leavesQty: number | null;
    avgPx: number | null;
    transactTime: Instant | undefined;
    instrument: string;
    clOrdId: string | null;
    expireTime: Instant | undefined;
    /** Legs data for multi-leg orders, undefined for single-leg orders */
    legs: OrderLeg[] | undefined;
}

/**
 * trading-gateway reports `expired` (not yet in the trading-ws-client contract) whenever an order's
 * time-in-force ends. Only GTC/GTD orders truly expire; every other order is done for the day.
 */
export type BlotterDisplayStatus = OrderStatus | 'expired' | 'done-for-day';

export function getBlotterDisplayStatus(
    status: OrderStatus | 'expired',
    duration: OrderDuration | undefined,
): BlotterDisplayStatus {
    return status === 'expired' && duration !== 'gtc' && duration !== 'gtd' ? 'done-for-day' : status;
}

export const processRow = (row: OrderWithMetaData, t: TFunction): BlotterProcessedRow => {
    const lastUpdate = row.updates.length ? row.updates[row.updates.length - 1] : null;
    const executionUpdates = row.updates.filter(isOrderExecutionUpdate);
    const lastExecutionUpdate = executionUpdates.length ? executionUpdates[executionUpdates.length - 1] : null;
    const lastContraBrokerUpdate = executionUpdates.toReversed().find((update) => update.contraBrokers.length);

    return {
        actions: OPEN_ORDERS.includes(row.status) ? ['cancel'] : [], // Actions are handled by the ActionsCellRenderer
        createdAt: row.createdAt,
        status: getBlotterDisplayStatus(row.status, row.duration),
        orderStatus: row.status,
        side: t(`trading::blotter::side::${row.side}`),
        quantity: row.quantity,
        duration: row.duration,
        account: row.account ?? '',
        accountName: row.accountName,
        firmName: row.firmName,
        firmTradingSystemName: row.firmTradingSystemName,
        type: row.type,
        price: row.price,
        stopPrice: typeof row.stopPrice === 'number' && !Number.isNaN(row.stopPrice) ? row.stopPrice : null,
        id: row.id,
        updates: row.updates,
        lastError: row.lastError,
        lastContraBroker: lastContraBrokerUpdate?.contraBrokers[0]?.broker ?? null,
        lastShares: executionUpdates.toReversed().find((update) => update.quantity)?.quantity ?? null,
        lastPx: executionUpdates.toReversed().find((update) => update.price)?.price ?? null,
        leavesQty: lastExecutionUpdate?.quantityRemaining ?? null,
        avgPx: lastExecutionUpdate?.averagePrice ?? null,
        transactTime: lastUpdate?.timestamp, // todo check if should be lastExecutionUpdate?.timestamp
        instrument: instrumentToString(row.instrument),
        clOrdId: row.executorInfo?.clOrdId ?? null,
        expireTime: row.expireTime ?? undefined,
        legs: row.legs,
    };
};
